import React from 'react';
import { useIntl } from 'react-intl';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import Link from '../../Link';
import history from '../../../core/history';
import PeriodFilter from '../shared/PeriodFilter';
import KpiCard from '../shared/KpiCard';
import InfoTooltip from '../shared/InfoTooltip';
import DonutChart from '../shared/DonutChart';
import EvolutionChart from '../shared/EvolutionChart';
import { LoadingSkeleton, ErrorState, MessageState } from '../shared/ReportingStates';
import {
  formatMoney, formatCount, formatRate, formatDay, photoUrl, DASH,
} from '../shared/reportingFormat';
import {
  deriveDashboardView, buildEarningsLink, listingPath, VEHICLES_PATH, RESERVATIONS_PATH, ADD_VEHICLE_PATH,
} from './dashboardUrlState';
import messages from '../../../locale/jsonMessages';
import sh from '../shared/OwnerReporting.css';
import s from './OwnerDashboard.css';

const COMPARE_LABELS = { today: 'orpVsToday', '7d': 'orpVs7d', '30d': 'orpVs30d', year: 'orpVsYear' };
const REVENUE_TILES = [
  { key: 'upcoming', name: 'orpCardUpcoming', hint: 'orpCardUpcomingHint', help: 'orpHelpUpcoming', dot: 'dotUpcoming' },
  { key: 'pendingPayout', name: 'orpCardPending', hint: 'orpCardPendingHint', help: 'orpHelpPending', dot: 'dotPending' },
  { key: 'paid', name: 'orpCardPaid', hint: 'orpCardPaidHint', help: 'orpHelpPaid', dot: 'dotPaid' },
];
// Colors of the reservation split (theme variables, see public/css/variables.css).
const SPLIT_SEGMENTS = [
  { key: 'completed', label: 'odbSplitCompleted', cssVar: '--approved-status-bg' },
  { key: 'upcoming', label: 'odbSplitUpcoming', cssVar: '--primary-link-foreground' },
  { key: 'cancelled', label: 'odbSplitCancelled', cssVar: '--cancelled-status-bg' },
];
const STATE_PILLS = {
  completed: { label: 'odbStatusCompleted', className: 'pillCompleted' },
  upcoming: { label: 'orpStatusUpcoming', className: 'pillUpcoming' },
  cancelled: { label: 'orpStatusCancelled', className: 'pillCancelled' },
};

const Thumb = ({ photo }) => {
  const url = photoUrl(photo);
  return <span className={s.thumb} style={url ? { backgroundImage: `url("${url}")` } : undefined} aria-hidden="true" />;
};

// "My revenue": net revenue of the period and its split by payout status (all values come from the server).
const RevenuePanel = ({ revenue, currency, earningsLink }) => {
  const { formatMessage, locale } = useIntl();
  return (
    <div className={sh.panel}>
      <h3 className={sh.panelTitle}><span className={sh.panelTitleText}>{formatMessage(messages.odbRevenueTitle)}<InfoTooltip text={formatMessage(messages.odbHelpRevenue)} /></span></h3>
      <div className={s.revenueTop}>
        <div>
          <p className={s.revenueValue}>{formatMoney(revenue.netRevenue, currency, locale)}</p>
          <p className={s.revenueCaption}>{formatMessage(messages.odbRevenueCaption)}</p>
        </div>
        <Link to={earningsLink} className={sh.secondaryButton}>{formatMessage(messages.odbSeePayouts)} <span aria-hidden="true">&rarr;</span></Link>
      </div>
      <div className={s.statusRow}>
        {REVENUE_TILES.map((tile) => (
          <div key={tile.key} className={s.statusTile}>
            <span className={cx(s.statusDot, s[tile.dot])} aria-hidden="true" />
            <div className={s.statusBody}>
              <span className={s.statusName}>{formatMessage(messages[tile.name])}<InfoTooltip text={formatMessage(messages[tile.help])} /></span>
              <span className={s.statusAmount}>{formatMoney(revenue[tile.key], currency, locale)}</span>
              <span className={s.statusHint}>{formatMessage(messages[tile.hint])}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// "Performance of my vehicles": at most 10 lines (server), best net revenue first. A line is clickable (opens the listing); the
// title is a real link so the keyboard and screen readers get it too.
const VehiclesPanel = ({ vehicles, vehicleCount, currency }) => {
  const { formatMessage, locale } = useIntl();
  const openListing = (event, listId) => {
    if (event.target && event.target.closest && event.target.closest('a')) return; // the link navigates by itself
    history.push(listingPath(listId));
  };
  return (
    <div className={sh.panel}>
      <div className={s.panelHead}>
        <h3 className={cx(sh.panelTitle, s.panelHeading)}><span className={sh.panelTitleText}>{formatMessage(messages.odbVehiclesTitle)}<InfoTooltip text={formatMessage(messages.odbHelpVehicles)} /></span></h3>
        <Link to={VEHICLES_PATH} className={s.panelLink}>{formatMessage(messages.odbSeeAllVehicles)} <span aria-hidden="true">&rarr;</span></Link>
      </div>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <caption>{formatMessage(messages.odbVehiclesTableLabel)}</caption>
          <thead>
            <tr>
              <th scope="col">{formatMessage(messages.orpColVehicle)}</th>
              <th scope="col" className={s.numeric}>{formatMessage(messages.odbColBookings)}</th>
              <th scope="col" className={s.numeric}>{formatMessage(messages.odbColRentedDays)}</th>
              <th scope="col">{formatMessage(messages.odbColOccupancy)}</th>
              <th scope="col" className={s.numeric}>{formatMessage(messages.orpColNet)}</th>
              <th scope="col" className={s.chevronCell}><span className={sh.srOnly}>{formatMessage(messages.odbOpenListing)}</span></th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((vehicle) => {
              // the bar only draws the rate returned by the server (kept between 0 and 100 for the drawing)
              const fill = Math.max(0, Math.min(100, Number(vehicle.occupancy) || 0));
              return (
                <tr key={vehicle.listId} className={s.clickableRow} onClick={(event) => openListing(event, vehicle.listId)}>
                  <td data-label={formatMessage(messages.orpColVehicle)}>
                    <span className={s.vehicle}>
                      <Thumb photo={vehicle.photo} />
                      <span className={s.vehicleText}>
                        <Link to={listingPath(vehicle.listId)} className={s.vehicleLink}>
                          {vehicle.title || formatMessage(messages.orpVehicleFallback, { id: vehicle.listId })}
                        </Link>
                        {vehicle.isOnline === false && <span className={s.pausedTag}>{formatMessage(messages.odbVehiclePaused)}</span>}
                      </span>
                    </span>
                  </td>
                  <td data-label={formatMessage(messages.odbColBookings)} className={s.numeric}>{formatCount(vehicle.bookings, locale)}</td>
                  <td data-label={formatMessage(messages.odbColRentedDays)} className={s.numeric}>{formatMessage(messages.odbDaysValue, { count: Number(vehicle.rentedDays) || 0 })}</td>
                  <td data-label={formatMessage(messages.odbColOccupancy)}>
                    <span className={s.occupancy}>
                      <span className={s.occupancyValue}>{formatRate(vehicle.occupancy, locale)}</span>
                      <span className={s.bar} aria-hidden="true"><span className={s.barFill} style={{ width: `${fill}%` }} /></span>
                    </span>
                  </td>
                  <td data-label={formatMessage(messages.orpColNet)} className={cx(s.numeric, s.strong)}>{formatMoney(vehicle.netRevenue, currency, locale)}</td>
                  <td className={s.chevronCell} aria-hidden="true">&rsaquo;</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {Number(vehicleCount) > vehicles.length && (
        <p className={s.panelNote}>{formatMessage(messages.odbVehiclesShown, { shown: vehicles.length, total: formatCount(vehicleCount, locale) })}</p>
      )}
    </div>
  );
};

// "Recent activity": the 5 latest reservations of the period (server), client as "First name X.", status pill, net amount.
const ActivityPanel = ({ activity, currency }) => {
  const { formatMessage, locale } = useIntl();
  return (
    <div className={sh.panel}>
      <div className={s.panelHead}>
        <h3 className={cx(sh.panelTitle, s.panelHeading)}><span className={sh.panelTitleText}>{formatMessage(messages.odbActivityTitle)}<InfoTooltip text={formatMessage(messages.odbHelpActivity)} /></span></h3>
        <Link to={RESERVATIONS_PATH} className={s.panelLink}>{formatMessage(messages.odbSeeAllReservations)} <span aria-hidden="true">&rarr;</span></Link>
      </div>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <caption>{formatMessage(messages.odbActivityTableLabel)}</caption>
          <thead>
            <tr>
              <th scope="col">{formatMessage(messages.orpColVehicle)}</th>
              <th scope="col">{formatMessage(messages.odbColGuest)}</th>
              <th scope="col">{formatMessage(messages.odbColPeriod)}</th>
              <th scope="col">{formatMessage(messages.orpColStatus)}</th>
              <th scope="col" className={s.numeric}>{formatMessage(messages.orpColNet)}</th>
            </tr>
          </thead>
          <tbody>
            {activity.map((item) => {
              const pill = STATE_PILLS[item.state];
              return (
                <tr key={item.reservationId}>
                  <td data-label={formatMessage(messages.orpColVehicle)}>
                    <span className={s.vehicle}>
                      <Thumb photo={item.photo} />
                      <span className={s.vehicleTitle}>{item.title || formatMessage(messages.orpVehicleFallback, { id: item.listId })}</span>
                    </span>
                  </td>
                  <td data-label={formatMessage(messages.odbColGuest)}>{item.guestName || DASH}</td>
                  <td data-label={formatMessage(messages.odbColPeriod)} className={s.period}>{formatDay(item.checkIn, locale, 'short')} &rarr; {formatDay(item.checkOut, locale, 'medium')}</td>
                  <td data-label={formatMessage(messages.orpColStatus)}>
                    {pill ? <span className={cx(s.pill, s[pill.className])}>{formatMessage(messages[pill.label])}</span> : DASH}
                  </td>
                  <td data-label={formatMessage(messages.orpColNet)} className={cx(s.numeric, s.strong)}>{formatMoney(item.netRevenue, currency, locale)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// "Operational indicators": numbers returned by the server, one tile each with its explanation.
const OperationalPanel = ({ operational, indicators }) => {
  const { formatMessage, locale } = useIntl();
  const days = (count) => formatMessage(messages.odbDaysValue, { count: Number(count) || 0 });
  const tiles = [
    { key: 'online', label: 'odbOpOnline', help: 'odbHelpOpOnline', value: formatCount(operational.onlineVehicles, locale) },
    { key: 'paused', label: 'odbOpPaused', help: 'odbHelpOpPaused', value: formatCount(operational.pausedVehicles, locale) },
    { key: 'bookings', label: 'odbOpBookings', help: 'odbHelpBookings', value: formatCount(indicators.bookings && indicators.bookings.value, locale) },
    { key: 'total', label: 'odbOpTotalDuration', help: 'odbHelpOpTotalDuration', value: days(operational.totalDuration) },
    { key: 'average', label: 'odbOpAverageDuration', help: 'odbHelpOpAverageDuration', value: days(operational.averageDuration) },
    { key: 'occupancy', label: 'odbKpiOccupancy', help: 'odbHelpOccupancy', value: formatRate(indicators.occupancy && indicators.occupancy.value, locale) },
  ];
  return (
    <div className={sh.panel}>
      <h3 className={sh.panelTitle}><span className={sh.panelTitleText}>{formatMessage(messages.odbOperationalTitle)}<InfoTooltip text={formatMessage(messages.odbHelpOperational)} /></span></h3>
      <dl className={s.opGrid}>
        {tiles.map((tile) => (
          <div key={tile.key} className={s.opTile}>
            <dt className={s.opLabel}>{formatMessage(messages[tile.label])}<InfoTooltip text={formatMessage(messages[tile.help])} /></dt>
            <dd className={s.opValue}>{tile.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// "Owner dashboard". Everything displayed is what getOwnerDashboard returns; the page only formats it (no amount is computed).
// Props: state (filters from the URL), loading, networkError, dashboard (query result), onChange(patch), onRetry, onReset.
const DashboardPage = ({ state, loading, networkError, dashboard, onChange, onRetry, onReset }) => {
  const { formatMessage, locale } = useIntl();
  const view = deriveDashboardView({ loading, networkError, dashboard, state });
  const currency = dashboard && dashboard.convertCurrency;
  const refreshing = Boolean(loading && dashboard);

  // The vehicle filter lists ALL the vehicles of the owner (vehicleOptions). While they are not known yet, the vehicle of the URL
  // is still offered so the list shows the right choice.
  const options = ((dashboard && dashboard.vehicleOptions) || []).map((vehicle) => ({
    id: vehicle.listId, title: vehicle.title || formatMessage(messages.orpVehicleFallback, { id: vehicle.listId }),
  }));
  if (state.listId && !options.some((vehicle) => Number(vehicle.id) === Number(state.listId))) {
    options.push({ id: state.listId, title: formatMessage(messages.orpVehicleFallback, { id: state.listId }) });
  }
  const noVehicle = Boolean(dashboard && Array.isArray(dashboard.vehicleOptions) && dashboard.vehicleOptions.length === 0 && !state.listId);

  const header = (
    <div className={sh.header}>
      <div className={sh.headerText}>
        <h1 className={sh.pageTitle}>{formatMessage(messages.odbTitle)}</h1>
        <p className={sh.pageSubtitle}>{formatMessage(messages.odbSubtitle)}</p>
      </div>
      {view !== 'notLoggedIn' && (
        <PeriodFilter period={state.period} startDate={state.startDate} endDate={state.endDate} listId={state.listId}
          vehicles={options} onChange={onChange} />
      )}
    </div>
  );

  let body;
  if (view === 'loading') {
    body = <LoadingSkeleton cards={6} panels={2} rows={3} label={formatMessage(messages.odbLoading)} />;
  } else if (view === 'notLoggedIn') {
    body = <MessageState title={formatMessage(messages.orpNotOwnerTitle)} text={(dashboard && dashboard.errorMessage) || formatMessage(messages.odbNotOwnerText)} />;
  } else if (view === 'error') {
    body = <ErrorState title={formatMessage(messages.odbErrorTitle)} message={dashboard && dashboard.errorMessage} onRetry={onRetry} />;
  } else if (view === 'empty') {
    // Without any vehicle: invite to add one. With vehicles: simply nothing in this period.
    body = noVehicle
      ? (
        <MessageState title={formatMessage(messages.orpEmptyNoVehicleTitle)} text={formatMessage(messages.orpEmptyNoVehicleText)}>
          <Link to={ADD_VEHICLE_PATH} className={sh.primaryButton}>{formatMessage(messages.orpAddVehicle)}</Link>
        </MessageState>
      )
      : <MessageState title={formatMessage(messages.odbEmptyTitle)} text={formatMessage(messages.odbEmptyText)} />;
  } else if (view === 'noResults') {
    body = (
      <MessageState title={formatMessage(messages.odbNoResultsTitle)} text={formatMessage(messages.odbNoResultsText)}>
        <button type="button" className={sh.secondaryButton} onClick={onReset}>{formatMessage(messages.orpResetFilters)}</button>
      </MessageState>
    );
  } else {
    const {
      indicators = {}, revenue = {}, series, period = {}, vehicles = [], vehicleCount, reservationsSplit = {}, recentActivity = [], operational = {},
    } = dashboard;
    const compareKey = COMPARE_LABELS[period.preset];
    const compare = compareKey ? formatMessage(messages[compareKey]) : formatMessage(messages.orpVsCustom, { days: period.previousDays });
    const days = (count) => formatMessage(messages.odbDaysValue, { count: Number(count) || 0 });
    // Center of the donut: number of reservations of the split (a count of what the server returns, no amount)
    const splitTotal = SPLIT_SEGMENTS.reduce((sum, segment) => sum + (Number(reservationsSplit[segment.key]) || 0), 0);
    body = (
      <div className={cx(refreshing && sh.refreshing)} aria-busy={refreshing}>
        <div className={sh.cardGrid}>
          <KpiCard accent label={formatMessage(messages.orpKpiNetRevenue)} help={formatMessage(messages.orpHelpNetRevenue)}
            value={formatMoney(indicators.netRevenue && indicators.netRevenue.value, currency, locale)} indicator={indicators.netRevenue} compareLabel={compare} />
          <KpiCard label={formatMessage(messages.orpKpiVolume)} help={formatMessage(messages.orpHelpVolume)}
            value={formatMoney(indicators.volume && indicators.volume.value, currency, locale)} indicator={indicators.volume} compareLabel={compare} />
          <KpiCard label={formatMessage(messages.odbKpiBookings)} help={formatMessage(messages.odbHelpBookings)}
            value={formatCount(indicators.bookings && indicators.bookings.value, locale)} indicator={indicators.bookings} compareLabel={compare} />
          <KpiCard label={formatMessage(messages.odbKpiRentedDays)} help={formatMessage(messages.odbHelpRentedDays)}
            value={days(indicators.rentedDays && indicators.rentedDays.value)} indicator={indicators.rentedDays} compareLabel={compare} />
          <KpiCard label={formatMessage(messages.odbKpiOccupancy)} help={formatMessage(messages.odbHelpOccupancy)}
            value={formatRate(indicators.occupancy && indicators.occupancy.value, locale)} indicator={indicators.occupancy} compareLabel={compare} />
          <KpiCard label={formatMessage(messages.odbKpiCancellations)} help={formatMessage(messages.odbHelpCancellations)}
            value={formatCount(indicators.cancellations && indicators.cancellations.value, locale)} indicator={indicators.cancellations} compareLabel={compare} />
        </div>

        <div className={sh.panelGrid}>
          <RevenuePanel revenue={revenue} currency={currency} earningsLink={buildEarningsLink(state)} />
          <EvolutionChart series={series} granularity={state.granularity} currency={currency}
            help={formatMessage(messages.orpHelpEvolution)} onGranularityChange={(granularity) => onChange({ granularity })} />
        </div>

        <div className={s.mainGrid}>
          <VehiclesPanel vehicles={vehicles} vehicleCount={vehicleCount} currency={currency} />
          <DonutChart title={formatMessage(messages.odbSplitTitle)} help={formatMessage(messages.odbHelpSplit)}
            segments={SPLIT_SEGMENTS.map((segment) => ({
              key: segment.key, label: formatMessage(messages[segment.label]), value: Number(reservationsSplit[segment.key]) || 0, cssVar: segment.cssVar,
            }))}
            total={splitTotal} totalLabel={formatMessage(messages.odbSplitTotal)} />
        </div>

        <div className={s.mainGrid}>
          <ActivityPanel activity={recentActivity} currency={currency} />
          <OperationalPanel operational={operational} indicators={indicators} />
        </div>
      </div>
    );
  }

  return <div className={sh.page}>{header}{body}</div>;
};

export default withStyles(sh, s)(DashboardPage);
